import { useEffect, useMemo, useState } from "react";
import React from "react";
import { api, API } from "@/lib/api";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  TrendingUp, DollarSign, Users, Wrench, Package, Building2, Percent, FileText,
  ChevronDown, ChevronRight, Download, Printer, FileSpreadsheet, Filter, Search, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const money = (v) => `$${(Number(v) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const moneyK = (v) => `$${(Number(v) || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
const pct = (v) => `${(Number(v) || 0).toFixed(1)}%`;
const fmtDate = (v) => {
  const d = (v || "").slice(0, 10);
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return y && m && day ? `${day}-${m}-${y}` : d;
};

const GRANS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
  { key: "ytd", label: "YTD" },
];

const GRAN_TITLE = { weekly: "Weekly Cashflow", monthly: "Monthly Cashflow", yearly: "Yearly Cashflow", ytd: "Year-to-Date Cashflow" };

function marginTone(margin, targets) {
  const t = targets?.target_margin ?? 30;
  const w = targets?.warning_margin ?? 15;
  if (margin >= t) return "text-emerald-600 bg-emerald-50";
  if (margin >= w) return "text-amber-600 bg-amber-50";
  return "text-red-600 bg-red-50";
}

function Kpi({ label, value, icon: Icon, tone = "bg-slate-100 text-slate-700", testid, accent, onClick }) {
  return (
    <div data-testid={testid} onClick={onClick}
      className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm transition-all ${onClick ? "cursor-pointer hover:border-navy/40 hover:shadow-md" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
        {onClick && <Search className="w-3.5 h-3.5 text-slate-300 print:hidden" />}
      </div>
      <p className={`text-2xl font-heading font-bold ${accent || "text-navy"}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}{onClick && <span className="text-navy/50 print:hidden"> · tap to verify</span>}</p>
    </div>
  );
}

const RECON_LABELS = {
  prior_work: { label: "Prior-period work billed here", tone: "bg-indigo-100 text-indigo-700" },
  manual_line: { label: "Manual / non-docket line", tone: "bg-purple-100 text-purple-700" },
  not_billed: { label: "Not yet billed", tone: "bg-amber-100 text-amber-700" },
  billed_elsewhere: { label: "Billed in another period", tone: "bg-slate-200 text-slate-700" },
};

function ReconciliationPanel({ rec }) {
  const [open, setOpen] = useState(false);
  if (!rec) return null;
  const gap = Number(rec.gap) || 0;
  const reconciled = Math.abs(gap) < 0.5;
  const Row = ({ label, value, sign, strong }) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className={strong ? "font-semibold text-navy" : "text-slate-600"}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-bold text-navy" : sign === "+" ? "text-emerald-600" : sign === "-" ? "text-rose-600" : "text-navy"}`}>
        {sign === "+" ? "+ " : sign === "-" ? "− " : ""}{money(Math.abs(Number(value) || 0))}
      </span>
    </div>
  );
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="cashflow-reconciliation">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-navy">Earned ↔ Invoiced Reconciliation</h2>
          <p className="text-xs text-slate-500 mt-0.5">Why accrual (work date) differs from what was billed (invoice date) this period · ex GST</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${reconciled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {reconciled ? "Reconciled" : `Gap ${money(gap)}`}
        </span>
      </div>
      <div className="px-5 py-4">
        <Row label="Earned Revenue (work in period)" value={rec.earned_ex_gst} strong />
        <div className="border-t border-dashed border-slate-100 my-1" />
        <Row label="Prior-period work billed this period" value={rec.prior_period_work_billed_here} sign="+" />
        <Row label="Manual / non-docket invoice lines" value={rec.manual_or_unmatched_lines} sign="+" />
        <Row label="Work this period not yet billed" value={rec.work_not_yet_billed} sign="-" />
        <Row label="Work this period billed in another period" value={rec.work_billed_in_other_period} sign="-" />
        <div className="border-t border-slate-200 my-1" />
        <Row label="Invoiced Revenue (invoice date in period, ex GST)" value={rec.invoiced_ex_gst} strong />
        <p className="text-[11px] text-slate-400 mt-2">Invoiced inc GST: {money(rec.invoiced_inc_gst)}</p>
        {(rec.items || []).length > 0 && (
          <button data-testid="recon-toggle-items" onClick={() => setOpen((o) => !o)}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {open ? "Hide" : "Show"} {rec.items.length} adjusting item{rec.items.length === 1 ? "" : "s"}
          </button>
        )}
        {open && (
          <div className="mt-3 overflow-x-auto" data-testid="recon-items">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-2 py-2">Reason</th>
                  <th className="text-left px-2 py-2">Invoice</th>
                  <th className="text-left px-2 py-2">Customer</th>
                  <th className="text-left px-2 py-2">Docket</th>
                  <th className="text-left px-2 py-2">Work Date</th>
                  <th className="text-left px-2 py-2">Invoice Date</th>
                  <th className="text-right px-2 py-2">Amount (ex GST)</th>
                </tr>
              </thead>
              <tbody>
                {rec.items.map((it, i) => {
                  const meta = RECON_LABELS[it.type] || { label: it.type, tone: "bg-slate-100 text-slate-600" };
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.tone}`}>{meta.label}</span></td>
                      <td className="px-2 py-2 text-slate-600">{it.invoice_number || "—"}</td>
                      <td className="px-2 py-2 text-slate-600">{it.customer || "—"}</td>
                      <td className="px-2 py-2 text-slate-600">{it.docket_number || "—"}</td>
                      <td className="px-2 py-2 text-slate-600">{it.work_date || "—"}</td>
                      <td className="px-2 py-2 text-slate-600">{it.invoice_date || "—"}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-navy">{money(it.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


export default function Cashflow() {
  const [gran, setGran] = useState("weekly");
  const [periodValue, setPeriodValue] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [technician, setTechnician] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState(null);

  // Reset the chosen period when the granularity changes (backend picks the current period by default).
  const changeGran = (g) => { setGran(g); setPeriodValue(""); };

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ granularity: gran });
    if (periodValue) params.set("period_value", periodValue);
    if (customerId) params.set("customer_id", customerId);
    if (technician) params.set("technician", technician);
    api.get(`/reports/cashflow?${params.toString()}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Could not load cashflow. Please try again."); });
  };
  useEffect(load, [gran, periodValue, customerId, technician]);

  const periodOptions = data?.period_options || [];
  const scopeLabel = gran === "ytd"
    ? "All data since tracking began"
    : (periodOptions.find((o) => o.value === (periodValue || data?.period_value))?.label
       || (data?.range?.start ? `${data.range.start} → ${data.range.end}` : ""));

  const s = data?.summary;
  const targets = data?.targets;

  const invoiceRows = data?.sources?.invoices || [];
  const superRows = data?.sources?.super || [];
  const docketRows = useMemo(
    () => (data?.periods || []).flatMap((p) => (p.invoices || []).map((r) => ({ ...r, period: p.label }))),
    [data]
  );

  const buildDetail = (key) => {
    if (!s) return null;
    const D = (extra) => ({ key, ...extra });
    switch (key) {
      case "earned_revenue":
        return D({ title: "Earned Revenue (Approved Work)", kind: "dockets", amountKey: "revenue", amountLabel: "Revenue (ex GST)", rows: docketRows, total: s.earned_revenue,
          formula: "The value of every approved service day, priced from its charge codes (ex GST). This is work you've done — whether or not it's been invoiced yet." });
      case "outstanding_to_invoice":
        return D({ title: "Outstanding to Invoice", kind: "dockets", amountKey: "revenue", amountLabel: "Revenue (ex GST)", rows: docketRows.filter((r) => r.stage === "earned"), total: s.outstanding_to_invoice,
          formula: "Approved work that is NOT yet on an invoice (ex GST). = Earned Revenue − work already invoiced. Each row below is a completed day still waiting to be billed." });
      case "labour_cost":
        return D({ title: "Labour Cost", kind: "dockets", amountKey: "labour_cost", amountLabel: "Labour", rows: docketRows.filter((r) => r.labour_cost), total: s.labour_cost,
          formula: "Each day's hours × that day's worker rate, summed across all approved days." });
      case "material_cost":
        return D({ title: "Material Cost", kind: "dockets", amountKey: "material_cost", amountLabel: "Materials", rows: docketRows.filter((r) => r.material_cost), total: s.material_cost,
          formula: "The material cost recorded on each approved day, summed." });
      case "overhead_cost":
        return D({ title: "Management Overhead", kind: "dockets", amountKey: "overhead_cost", amountLabel: "Overhead", rows: docketRows.filter((r) => r.overhead_cost), total: s.overhead_cost,
          formula: "The overhead applied to each approved day (from your overhead rate in Settings), summed." });
      case "jobs_completed":
        return D({ title: "Total Approved Days", kind: "dockets", amountKey: null, amountLabel: "", rows: docketRows, total: null,
          formula: `Count of approved service days in this view (${docketRows.length}). Each row is one completed day.` });
      case "invoiced_revenue":
        return D({ title: "Invoiced Revenue (inc GST)", kind: "invoices", amountKey: "total_inc_gst", amountLabel: "Total (inc GST)", rows: invoiceRows.filter((i) => i.in_period !== false), total: s.invoiced_revenue,
          formula: "The total (inc GST) of invoices dated in the selected period (synced / sent / awaiting payment / paid)." });
      case "paid_revenue":
        return D({ title: "Cash Received (inc GST)", kind: "invoices", amountKey: "amount_paid", amountLabel: "Paid (inc GST)", rows: invoiceRows.filter((i) => i.amount_paid > 0), total: s.paid_revenue,
          formula: "The amount actually paid on each invoice (inc GST), from Xero. Part-payments are included. Refresh from Xero keeps this current." });
      case "outstanding_payment":
        return D({ title: "Outstanding Payment (inc GST)", kind: "invoices", dueCol: true, amountKey: "amount_due", amountLabel: "Due (inc GST)", rows: invoiceRows.filter((i) => i.status !== "paid" && i.amount_due > 0), total: s.outstanding_payment,
          formula: "The amount still owed on each unpaid invoice (inc GST), from Xero. This matches the Invoices Awaiting Payment page total." });
      case "gross_profit":
        return D({ title: "Gross Profit ($)", kind: "formula", total: s.gross_profit,
          lines: [["Earned Revenue", money(s.earned_revenue)], ["− Labour Cost", "−" + money(s.labour_cost)], ["− Material Cost", "−" + money(s.material_cost)], ["− Management Overhead", "−" + money(s.overhead_cost)], ["= Gross Profit", money(s.gross_profit)]],
          formula: "Gross Profit = Earned Revenue − Labour − Materials − Overhead (all ex GST)." });
      case "margin":
        return D({ title: "Gross Profit (%)", kind: "formula", total: null,
          lines: [["Gross Profit", money(s.gross_profit)], ["÷ Earned Revenue", money(s.earned_revenue)], ["= Margin", pct(s.margin)]],
          formula: "Margin % = Gross Profit ÷ Earned Revenue × 100." });
      case "avg_profit_per_job":
        return D({ title: "Avg Profit / Job", kind: "formula", total: null,
          lines: [["Gross Profit", money(s.gross_profit)], ["÷ Approved Days", String(s.jobs_completed || 0)], ["= Avg Profit / Job", money(s.avg_profit_per_job)]],
          formula: "Average Profit per approved day = Gross Profit ÷ number of approved days." });
      case "avg_invoice_value":
        return D({ title: "Avg Invoice Value", kind: "formula", total: null,
          lines: [["Earned Revenue", money(s.earned_revenue)], ["÷ Approved Days", String(s.jobs_completed || 0)], ["= Avg Value", money(s.avg_invoice_value)]],
          formula: "Average value per approved day = Earned Revenue ÷ number of approved days." });
      case "recovery_rate":
        return D({ title: "Recovery Rate", kind: "formula", total: null,
          lines: [["Earned Revenue", money(s.earned_revenue)], ["÷ Total Cost", money((s.labour_cost || 0) + (s.material_cost || 0) + (s.overhead_cost || 0))], ["= Recovery Rate", pct(s.recovery_rate)]],
          formula: "Recovery Rate = Earned Revenue ÷ Total Cost × 100. Over 100% means the work earns more than it costs." });
      case "super":
        return D({ title: `Super Calculation (${Math.round((s.super_rate || 0.12) * 100)}%)`, kind: "super",
          rows: superRows, total: s.super,
          formula: `Superannuation Guarantee — ${Math.round((s.super_rate || 0.12) * 100)}% of wages (labour cost), paid by the employer ON TOP of wages (not deducted from workers). Shown per worker for the selected period; backdated across all data.` });
      default:
        return null;
    }
  };
  const detailData = detail ? buildDetail(detail) : null;

  const chartData = useMemo(() => (data?.periods || []).map((p) => ({
    name: p.label,
    Revenue: p.revenue,
    Labour: p.labour_cost,
    Materials: p.material_cost,
    Overhead: p.overhead_cost,
    "Gross Profit": p.gross_profit,
  })), [data]);

  const exportUrl = (kind) => {
    const params = new URLSearchParams({ granularity: gran });
    if (periodValue) params.set("period_value", periodValue);
    if (customerId) params.set("customer_id", customerId);
    if (technician) params.set("technician", technician);
    return `${API}/reports/cashflow/${kind}?${params.toString()}`;
  };
  const download = (kind) => {
    const token = localStorage.getItem("ts_token");
    const suffix = periodValue || data?.period_value || "";
    fetch(exportUrl(kind), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("export failed"); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `cashflow-${gran}${suffix ? "-" + suffix : ""}.${kind === "excel" ? "xlsx" : "pdf"}`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error(`Could not export ${kind.toUpperCase()}. Please try again.`));
  };

  return (
    <div className="space-y-6 animate-fade-up print:space-y-4" data-testid="cashflow-page">
      <div className="flex flex-wrap items-start justify-between gap-4 print:block">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">{GRAN_TITLE[gran] || "Cashflow"}</h1>
          <p className="text-slate-500 mt-1">Live snapshot from approved daily dockets · Earned → Invoiced → Paid{scopeLabel ? ` · ${scopeLabel}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <button data-testid="export-pdf-btn" onClick={() => download("pdf")}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button data-testid="export-excel-btn" onClick={() => download("excel")}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50 transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button data-testid="print-btn" onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-navy hover:bg-slate-50 transition-colors">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden" data-testid="granularity-toggle">
          {GRANS.map((g) => (
            <button key={g.key} data-testid={`gran-${g.key}`} onClick={() => changeGran(g.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${gran === g.key ? "bg-navy text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {g.label}
            </button>
          ))}
        </div>
        {gran !== "ytd" && periodOptions.length > 0 && (
          <select data-testid="period-picker" value={periodValue || data?.period_value || ""}
            onChange={(e) => setPeriodValue(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-navy bg-white font-medium">
            {periodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select data-testid="filter-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-navy bg-white">
            <option value="">All Customers</option>
            {(data?.filters?.customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-technician" value={technician} onChange={(e) => setTechnician(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-navy bg-white">
            <option value="">All Technicians</option>
            {(data?.filters?.technicians || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {loading || !s ? (
        <p className="text-slate-400">Loading cashflow…</p>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="cashflow-kpis">
            <Kpi testid="kpi-revenue" label="Earned Revenue (Approved Work)" value={money(s.earned_revenue ?? s.revenue)} icon={DollarSign} tone="bg-blue-100 text-blue-700" onClick={() => setDetail("earned_revenue")} />
            <Kpi testid="kpi-invoiced" label="Invoiced Revenue (inc GST)" value={money(s.invoiced_revenue)} icon={FileText} tone="bg-indigo-100 text-indigo-700" onClick={() => setDetail("invoiced_revenue")} />
            <Kpi testid="kpi-paid" label="Cash Received (inc GST · live)" value={money(s.paid_revenue)} icon={DollarSign} tone="bg-emerald-100 text-emerald-700" onClick={() => setDetail("paid_revenue")} />
            <Kpi testid="kpi-out-invoice" label="Outstanding to Invoice" value={money(s.outstanding_to_invoice)} icon={FileText} tone="bg-amber-100 text-amber-700" accent={s.outstanding_to_invoice > 0 ? "text-amber-600" : "text-navy"} onClick={() => setDetail("outstanding_to_invoice")} />
            <Kpi testid="kpi-out-payment" label="Outstanding Payment (inc GST · live)" value={money(s.outstanding_payment)} icon={DollarSign} tone="bg-rose-100 text-rose-700" accent={s.outstanding_payment > 0 ? "text-rose-600" : "text-navy"} onClick={() => setDetail("outstanding_payment")} />
            <Kpi testid="kpi-labour" label="Labour Cost" value={money(s.labour_cost)} icon={Users} tone="bg-orange-100 text-orange-700" onClick={() => setDetail("labour_cost")} />
            <Kpi testid="kpi-materials" label="Material Cost" value={money(s.material_cost)} icon={Package} tone="bg-purple-100 text-purple-700" onClick={() => setDetail("material_cost")} />
            <Kpi testid="kpi-overhead" label="Management Overhead" value={money(s.overhead_cost)} icon={Building2} tone="bg-slate-100 text-slate-700" onClick={() => setDetail("overhead_cost")} />
            <Kpi testid="kpi-super" label={`Super (${Math.round((s.super_rate || 0.12) * 100)}%) · employer-paid`} value={money(s.super)} icon={Users} tone="bg-cyan-100 text-cyan-700" onClick={() => setDetail("super")} />
            <Kpi testid="kpi-gross-profit" label="Gross Profit ($)" value={money(s.gross_profit)} icon={TrendingUp}
              tone="bg-emerald-100 text-emerald-700" accent={s.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"} onClick={() => setDetail("gross_profit")} />
            <Kpi testid="kpi-margin" label="Gross Profit (%)" value={pct(s.margin)} icon={Percent}
              tone="bg-emerald-100 text-emerald-700" accent={marginTone(s.margin, targets).split(" ")[0]} onClick={() => setDetail("margin")} />
            <Kpi testid="kpi-avg-profit" label="Avg Profit / Job" value={money(s.avg_profit_per_job)} icon={TrendingUp} tone="bg-teal-100 text-teal-700" onClick={() => setDetail("avg_profit_per_job")} />
            <Kpi testid="kpi-jobs" label="Total Jobs Completed" value={s.jobs_completed} icon={Wrench} tone="bg-indigo-100 text-indigo-700" onClick={() => setDetail("jobs_completed")} />
            <Kpi testid="kpi-avg-invoice" label="Avg Invoice Value" value={money(s.avg_invoice_value)} icon={FileText} tone="bg-sky-100 text-sky-700" onClick={() => setDetail("avg_invoice_value")} />
            <Kpi testid="kpi-recovery" label="Recovery Rate" value={pct(s.recovery_rate)} icon={Percent} tone="bg-amber-100 text-amber-700" onClick={() => setDetail("recovery_rate")} />
          </div>

          {/* Earned ↔ Invoiced reconciliation */}
          <ReconciliationPanel rec={data?.reconciliation} />

          {/* Graph */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="cashflow-chart">
            <h2 className="text-lg font-heading font-semibold text-navy mb-4">Profitability Trend</h2>
            {chartData.length === 0 ? (
              <p className="text-slate-400 text-sm py-12 text-center">No approved work for this selection yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tickFormatter={moneyK} tick={{ fontSize: 11, fill: "#64748b" }} width={70} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Labour" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Materials" fill="#a855f7" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Overhead" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="Gross Profit" stroke="#059669" strokeWidth={3} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Breakdown table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="cashflow-table">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-heading font-semibold text-navy">Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-3 font-semibold">Period</th>
                    <th className="text-right px-3 py-3 font-semibold">Jobs</th>
                    <th className="text-right px-3 py-3 font-semibold">Revenue</th>
                    <th className="text-right px-3 py-3 font-semibold">Labour</th>
                    <th className="text-right px-3 py-3 font-semibold">Materials</th>
                    <th className="text-right px-3 py-3 font-semibold">Overhead</th>
                    <th className="text-right px-3 py-3 font-semibold">Gross Profit</th>
                    <th className="text-right px-3 py-3 font-semibold">Margin</th>
                    <th className="text-right px-3 py-3 font-semibold">Avg Inv</th>
                    <th className="text-right px-4 py-3 font-semibold">Recovery</th>
                  </tr>
                </thead>
                <tbody>
                  {data.periods.length === 0 && (
                    <tr><td colSpan={10} className="text-center text-slate-400 py-10">No data for this selection.</td></tr>
                  )}
                  {data.periods.map((p) => (
                    <React.Fragment key={p.key}>
                      <tr data-testid={`period-row-${p.key}`}
                        onClick={() => setExpanded((e) => ({ ...e, [p.key]: !e[p.key] }))}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="px-4 py-3 font-semibold text-navy flex items-center gap-1">
                          {expanded[p.key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {p.label}
                        </td>
                        <td className="text-right px-3 py-3">{p.job_count}</td>
                        <td className="text-right px-3 py-3 font-medium">{money(p.revenue)}</td>
                        <td className="text-right px-3 py-3 text-slate-500">−{money(p.labour_cost)}</td>
                        <td className="text-right px-3 py-3 text-slate-500">−{money(p.material_cost)}</td>
                        <td className="text-right px-3 py-3 text-slate-500">−{money(p.overhead_cost)}</td>
                        <td className={`text-right px-3 py-3 font-semibold ${p.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(p.gross_profit)}</td>
                        <td className="text-right px-3 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${marginTone(p.margin, targets)}`}>{pct(p.margin)}</span>
                        </td>
                        <td className="text-right px-3 py-3 text-slate-600">{money(p.avg_invoice)}</td>
                        <td className="text-right px-4 py-3 text-slate-600">{pct(p.recovery_rate)}</td>
                      </tr>
                      {expanded[p.key] && (
                        <tr data-testid={`period-detail-${p.key}`}>
                          <td colSpan={10} className="bg-slate-50/60 px-4 py-3">
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-400 uppercase tracking-wider">
                                    <th className="text-left px-2 py-2">Docket / Day</th>
                                    <th className="text-left px-2 py-2">Stage</th>
                                    <th className="text-left px-2 py-2">Customer</th>
                                    <th className="text-left px-2 py-2">Site</th>
                                    <th className="text-left px-2 py-2">Technician</th>
                                    <th className="text-right px-2 py-2">Revenue</th>
                                    <th className="text-right px-2 py-2">Labour</th>
                                    <th className="text-right px-2 py-2">Materials</th>
                                    <th className="text-right px-2 py-2">Overhead</th>
                                    <th className="text-right px-2 py-2">Gross Profit</th>
                                    <th className="text-right px-2 py-2">Margin</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.invoices.map((r) => (
                                    <tr key={r.invoice_id} className="border-t border-slate-100">
                                      <td className="px-2 py-2 font-medium text-navy">{r.invoice_number}</td>
                                      <td className="px-2 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.stage === "paid" ? "bg-emerald-100 text-emerald-700" : r.stage === "invoiced" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
                                          {r.stage === "paid" ? "Paid" : r.stage === "invoiced" ? "Invoiced" : "Earned"}
                                        </span>
                                      </td>
                                      <td className="px-2 py-2">{r.customer || "—"}</td>
                                      <td className="px-2 py-2 text-slate-500 max-w-[180px] truncate">{r.site_address || "—"}</td>
                                      <td className="px-2 py-2 text-slate-500">{r.technician}</td>
                                      <td className="text-right px-2 py-2 font-medium">{money(r.revenue)}</td>
                                      <td className="text-right px-2 py-2 text-slate-500">−{money(r.labour_cost)}</td>
                                      <td className="text-right px-2 py-2 text-slate-500">−{money(r.material_cost)}</td>
                                      <td className="text-right px-2 py-2 text-slate-500">−{money(r.overhead_cost)}</td>
                                      <td className={`text-right px-2 py-2 font-semibold ${r.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(r.gross_profit)}</td>
                                      <td className="text-right px-2 py-2">
                                        <span className={`px-2 py-0.5 rounded-full font-semibold ${marginTone(r.margin, targets)}`}>{pct(r.margin)}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] overflow-auto p-0" data-testid="metric-detail-dialog">
          {detailData && (
            <>
              <div className="relative bg-navy text-white px-6 py-5 sticky top-0 z-10">
                <DialogTitle className="text-lg font-heading font-bold pr-8" data-testid="metric-detail-title">{detailData.title}</DialogTitle>
                <DialogDescription className="text-slate-300 text-sm mt-1 pr-8">{detailData.formula}</DialogDescription>
                <button onClick={() => setDetail(null)} data-testid="metric-detail-close"
                  className="absolute right-4 top-4 z-20 text-white/70 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {detailData.kind === "formula" ? (
                  <div className="max-w-md mx-auto space-y-1" data-testid="metric-detail-formula">
                    {detailData.lines.map(([lbl, val], i) => {
                      const isTotal = lbl.startsWith("=");
                      return (
                        <div key={i} className={`flex justify-between px-4 py-2 rounded-lg ${isTotal ? "bg-navy text-white font-bold mt-2" : "text-slate-600"}`}>
                          <span>{lbl}</span><span>{val}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="metric-detail-table">
                      <thead>
                        <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                          {detailData.kind === "invoices" ? (
                            <>
                              <th className="text-left px-2 py-2">Invoice</th>
                              <th className="text-left px-2 py-2">Customer</th>
                              <th className="text-left px-2 py-2">{detailData.dueCol ? "Due Date" : "Status"}</th>
                              {detailData.amountKey && <th className="text-right px-2 py-2">{detailData.amountLabel}</th>}
                            </>
                          ) : detailData.kind === "super" ? (
                            <>
                              <th className="text-left px-2 py-2">Worker</th>
                              <th className="text-right px-2 py-2">Wages (labour)</th>
                              <th className="text-right px-2 py-2">Super</th>
                            </>
                          ) : (
                            <>
                              <th className="text-left px-2 py-2">Day / Docket</th>
                              <th className="text-left px-2 py-2">Customer</th>
                              <th className="text-left px-2 py-2">Site</th>
                              <th className="text-left px-2 py-2">Date</th>
                              {detailData.amountKey && <th className="text-right px-2 py-2">{detailData.amountLabel}</th>}
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.rows.length === 0 && (
                          <tr><td className="px-2 py-6 text-slate-400 text-center" colSpan={detailData.kind === "invoices" ? 4 : detailData.kind === "super" ? 3 : 5}>No line items in this view.</td></tr>
                        )}
                        {detailData.rows.map((r, i) => (
                          <tr key={r.invoice_id || r.invoice_number || r.worker || i} className="border-b border-slate-50">
                            {detailData.kind === "invoices" ? (
                              <>
                                <td className="px-2 py-2 font-medium text-navy">{r.invoice_number}</td>
                                <td className="px-2 py-2">{r.customer}</td>
                                <td className="px-2 py-2"><span className="text-xs text-slate-500">{detailData.dueCol ? fmtDate(r.due_date) : (r.xero_status_display || r.status)}</span></td>
                                {detailData.amountKey && <td className="text-right px-2 py-2 font-medium">{money(r[detailData.amountKey])}</td>}
                              </>
                            ) : detailData.kind === "super" ? (
                              <>
                                <td className="px-2 py-2 font-medium text-navy">{r.worker}</td>
                                <td className="text-right px-2 py-2 text-slate-500">{money(r.wages)}</td>
                                <td className="text-right px-2 py-2 font-medium">{money(r.super)}</td>
                              </>
                            ) : (
                              <>
                                <td className="px-2 py-2 font-medium text-navy">{r.docket_number || r.invoice_number}</td>
                                <td className="px-2 py-2">{r.customer || "—"}</td>
                                <td className="px-2 py-2 text-slate-500 max-w-[160px] truncate">{r.site_address || "—"}</td>
                                <td className="px-2 py-2 text-slate-500">{r.date || r.period}</td>
                                {detailData.amountKey && <td className="text-right px-2 py-2 font-medium">{money(r[detailData.amountKey])}</td>}
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      {detailData.total != null && (
                        <tfoot>
                          <tr className="border-t-2 border-navy font-bold text-navy">
                            <td className="px-2 py-3" colSpan={detailData.kind === "invoices" ? 3 : detailData.kind === "super" ? 2 : 4}>
                              Total ({detailData.rows.length} {detailData.kind === "invoices" ? "invoice" : detailData.kind === "super" ? "worker" : "day"}{detailData.rows.length === 1 ? "" : "s"})
                            </td>
                            <td className="text-right px-2 py-3" data-testid="metric-detail-total">{money(detailData.total)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
